import { listSurfaceStatuses, type SurfaceStatus } from "../surfaces/registry.js";
import type { SurfaceName } from "../surfaces/names.js";
import type { OrgxWorkspace } from "./workspaces.js";
import {
  updateWizardState,
  type WizardContinuityDefaults,
  type WizardStateRecord,
} from "./wizard-state.js";

const REVIEW_SURFACE_ORDER: SurfaceName[] = [
  "claude",
  "cursor",
  "codex",
  "vscode",
  "windsurf",
  "zed",
  "chatgpt",
];

export interface ContinuitySeed {
  executionMode?: "cloud" | "local" | null;
  statuses?: SurfaceStatus[];
  workspace?: Pick<OrgxWorkspace, "id" | "name"> | null;
  workspaceName?: string | null;
}

function selectReviewSurface(statuses: SurfaceStatus[]): SurfaceName | undefined {
  for (const name of REVIEW_SURFACE_ORDER) {
    const configured = statuses.find((status) => status.name === name && status.configured);
    if (configured) {
      return configured.name;
    }
  }

  for (const name of REVIEW_SURFACE_ORDER) {
    const detected = statuses.find((status) => status.name === name && status.detected);
    if (detected) {
      return detected.name;
    }
  }

  return undefined;
}

function inferExecutionMode(
  explicitMode: "cloud" | "local" | null | undefined,
  statuses: SurfaceStatus[],
): "cloud" | "local" | undefined {
  if (explicitMode) {
    return explicitMode;
  }

  const openclaw = statuses.find((status) => status.name === "openclaw");
  if (openclaw?.configured || openclaw?.detected) {
    return "local";
  }

  const hostedSurface = statuses.find(
    (status) => status.name !== "openclaw" && (status.configured || status.detected),
  );

  return hostedSurface ? "cloud" : undefined;
}

export function inferContinuityDefaults(seed: ContinuitySeed = {}): WizardContinuityDefaults {
  const statuses = seed.statuses ?? listSurfaceStatuses();
  const continuity: WizardContinuityDefaults = {};

  const reviewSurface = selectReviewSurface(statuses);
  if (reviewSurface) {
    continuity.reviewSurface = reviewSurface;
  }

  const executionMode = inferExecutionMode(seed.executionMode, statuses);
  if (executionMode) {
    continuity.executionMode = executionMode;
  }

  if (seed.workspace?.id) {
    continuity.workspaceId = seed.workspace.id;
  }

  const workspaceName = seed.workspace?.name ?? seed.workspaceName ?? undefined;
  if (workspaceName?.trim()) {
    continuity.workspaceName = workspaceName.trim();
  }

  return continuity;
}

function mergeContinuityDefaults(
  current: WizardContinuityDefaults | undefined,
  next: WizardContinuityDefaults,
): WizardContinuityDefaults {
  return {
    ...(current ?? {}),
    ...next,
  };
}

export function persistContinuityDefaults(
  seed: ContinuitySeed = {},
  statePath?: string,
): WizardStateRecord {
  const next = inferContinuityDefaults(seed);

  return updateWizardState(
    (current) => ({
      ...current,
      continuity: mergeContinuityDefaults(current.continuity, next),
    }),
    statePath,
  );
}
